import './JobDatasets.scss';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, ReactElement, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { Loader } from '../../../../__legacy__/components/Loader';
import { GTable } from '../../../../__legacy__/components/table/GTable';
import { IGColumnProps } from '../../../../__legacy__/components/table/IGColumnProps';
import { toLocaleDateTimeString } from '../../../../__legacy__/helpers/date.helpers';
import { S_Alert } from '../../../../design/alert/S_Alert';
import { S_TabsCard } from '../../../../design/card/tabsCard/S_TabsCards';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { ColumnLayout } from '../../../common/layouts/columnLayout/ColumnLayout';
import { RowLayout } from '../../../common/layouts/rowLayout/RowLayout';
import { IJobDataset, JOB_TYPE } from '../../jobs.model';
import { getJobTranslationPrefix } from '../../utils';

interface IJobDatasetsProps {
  jobType: JOB_TYPE;
  datasets: IJobDataset[];
  datasetIds: string[];
  toggleDataset: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  toggleAllDatasets: () => Promise<void>;
  isLoading: boolean;
  isFormDisabled: boolean;
  isInferenceOnDataset: boolean;
}

export const JobDatasets = observer((props: IJobDatasetsProps): ReactElement => {
  const { jobType, datasets, isFormDisabled, datasetIds, toggleDataset, toggleAllDatasets, isLoading, isInferenceOnDataset } = props;
  const { t } = useTranslator();
  const translationPrefix = getJobTranslationPrefix(jobType);

  const columns = useMemo((): IGColumnProps<IJobDataset>[] => {
    return [
      {
        headerName: '',
        renderer: (item: IJobDataset) => (
          <S_Checkbox
            checked={datasetIds.includes(item.id)}
            onChange={(e) => {
              void toggleDataset(e);
            }}
            value={item.id}
            id={item.id}
            disabled={isFormDisabled}
          />
        ),
        headerRenderer: (): ReactElement => (
          <S_Checkbox
            checked={datasets.length === datasetIds.length && datasets.length > 0}
            onChange={() => {
              void toggleAllDatasets();
            }}
            disabled={isFormDisabled}
            id={'id-select-all'}
            value={'value-select-all'}
          />
        ),
      },
      {
        headerName: 'Name',
        renderer: (x) => x.name,
      },
      {
        headerName: 'Description',
        renderer: () => '',
      },
      {
        headerName: 'Images',
        renderer: (x) => x.imagesCount,
      },
      {
        headerName: 'Size (MB)',
        renderer: (item) => (
          <RowLayout size='xs'>
            {item.sizeInMB.toFixed(2)}
            {item.sizeInMB >= 1024 && (
              <FontAwesomeIcon
                title={t('dataset_size_limit_tooltip')}
                className='menu-warning-icon'
                icon={faTriangleExclamation}
              />
            )}
          </RowLayout>
        ),
      },
      {
        headerName: 'Split',
        renderer: ({ testImagesCount, trainImagesCount, validationImagesCount }: IJobDataset): ReactElement => {
          if (testImagesCount === undefined || trainImagesCount === undefined || validationImagesCount === undefined) {
            return <>{t('na')}</>;
          }

          return (
            <>
              {trainImagesCount}/{validationImagesCount}/{testImagesCount}
            </>
          );
        },
      },
      {
        headerName: 'Created',
        renderer: (item: IJobDataset) => <>{toLocaleDateTimeString(item.createdDate)}</>,
      },
    ];
  }, [datasets, datasetIds]);

  const className = 'job-datasets card-with-list';
  if (isLoading) {
    return (
      <S_TabsCard className={className}>
        <Loader isLoading={true} />
      </S_TabsCard>
    );
  }

  return (
    <S_TabsCard className={className}>
      <ColumnLayout size='md'>
        <div>
          <S_Alert color='info'>
            <div>{t(isInferenceOnDataset ? 'details_from_newest_dataset' : 'details_from_newest_project')}</div>
          </S_Alert>
          <h4>{t('select_from_published_datasets')}</h4>
          <Trans
            i18nKey={`${translationPrefix}.select_from_published_datasets_description`}
            components={{ bold: <strong /> }}
          />
        </div>
        <div>
          <div>
            <GTable
              isLoading={false}
              columns={columns}
              items={datasets}
              noItemsPlaceholder={t('no_datasets_in_project')}
            />
          </div>
        </div>
      </ColumnLayout>
    </S_TabsCard>
  );
});
